/**
 * Quantity Decrease Auto-Approval Rule (HCP-108022)
 *
 * When quantity is decreased on a confirmed order (reason 01/02), the quantity field
 * should be promoted to auto-confirmable since slot validation is skipped.
 *
 * This rule applies AFTER the slot management rules, overriding any
 * 'requireDispatchConfirmation' state for the quantity field when the
 * quantity-decrease-on-confirmed-order condition is met.
 */

import {useMemo} from 'react'

import {QuantitySelection} from '../../../../fields/quantity/types'
import {useOrderProcessFormContext} from '../../../../form/useOrderProcessFormContext'
import {ChangeableFields} from '../../changeableFields/types'

import {isQuantityOnlyDecreaseOnConfirmedOrder} from '../hooks/isQuantityOnlyDecreaseOnConfirmedOrder'

export interface UseQuantityDecreaseAutoApprovalRulesOptions {
  /** Whether the order is confirmed by customer */
  isOrderConfirmedByCustomer: boolean
}

/**
 * Hook that promotes quantity field to auto-confirmable when quantity is decreased
 * on a confirmed order. This ensures the visual indicator shows green (auto-approved)
 * rather than orange (requires dispatch confirmation).
 *
 * @param changeableFields - Fields after slot management rules
 * @param options - Configuration including order confirmation status
 * @returns Adjusted changeable fields
 */
export const useQuantityDecreaseAutoApprovalRules = (
  changeableFields: ChangeableFields,
  {isOrderConfirmedByCustomer}: UseQuantityDecreaseAutoApprovalRulesOptions
): ChangeableFields => {
  const {watch, formState} = useOrderProcessFormContext()
  const currentQuantity = watch('quantity') as QuantitySelection | undefined
  const originalQuantity = formState.defaultValues?.quantity as QuantitySelection | undefined

  return useMemo(() => {
    const shouldAutoApprove = isQuantityOnlyDecreaseOnConfirmedOrder({
      isOrderConfirmedByCustomer,
      changeableFields,
      currentQuantity,
      originalQuantity
    })

    if (!shouldAutoApprove) return changeableFields

    // Promote quantity and spacingOrFlowRate fields to auto-confirmable
    // spacingOrFlowRate is included because it gets auto-adjusted by
    // useLoadSpacingAdjustment as a side effect of quantity changes
    return changeableFields.map((field) => {
      const fieldName =
        typeof field.fieldName === 'string' ? field.fieldName : field.fieldName.field
      const isQuantityField = fieldName === 'quantity' || fieldName.startsWith('quantity.')
      const isSpacingField = fieldName === 'spacingOrFlowRate'

      if (!isQuantityField && !isSpacingField) return field
      if (field.state !== 'requireDispatchConfirmation') return field

      return {
        ...field,
        state: 'autoConfirmable' as const
      }
    })
  }, [isOrderConfirmedByCustomer, changeableFields, currentQuantity, originalQuantity])
}
